/**
 * ClipboardController - Clipboard and copy/paste management
 * 
 * Handles all clipboard operations:
 * - Single device copy/paste (internal clipboard)
 * - Multi-device selection copy/paste (internal + system clipboard)
 * - Cross-tab clipboard sharing via system clipboard
 * - Template deduplication during paste
 * - Undo/redo integration for paste operations
 * - Orchestration (performCopy/Cut/Paste)
 * - Button handlers (copySelectedToClipboard, pasteFromClipboard, etc.)
 * - Device export/import (.pw files)
 */

import { generateId } from '../utils/index.js';
import { getSelectionBounds } from '../utils/geometry.js';
import { showToast } from './ToastController.js';
import { screenToCanvas } from './ViewportController.js';
import {
    toShareString,
    fromShareString,
    createPatchData,
    extractSubset
} from '../utils/serialization.js';
import {
    PATCHWERK_URL_PREFIX_PROD,
    PATCHWERK_URL_PREFIX_DEV
} from '../constants/index.js';
import {
    getSelectedDevices,
    hasDeviceSelection,
    hasMultipleDevicesSelected,
    setDeviceSelection,
    clearDeviceSelection,
    updateSelectionVisuals,
    getLibraryMultiSelect,
    getSingleSelectedDevice
} from './SelectionController.js';
import { deleteSelectedDevices } from './DeletionController.js';

// ============= STATE =============

// Internal clipboard for single device copy/paste (linked instances)
let clipboard = null;

// Selection clipboard for multi-device copy/paste (within same tab)
let selectionClipboard = null;

// Module dependencies (injected via init)
let instanceModel = null;
let definitionModel = null;
let connectionModel = null;
let renderDeviceFunc = null;
let renderAllCablesFunc = null;
let updateConnectedPortsFunc = null;
let renderLibraryFunc = null;
let scheduleAutosaveFunc = null;
let recordActionFunc = null;
let selectDeviceFunc = null;
let updateSelectionButtonsFunc = null;
let getTemplateForDeviceFunc = null;
let getConnectionSourceGroupFunc = null;
let getConnectionTargetGroupFunc = null;
let findOpenPositionFunc = null;
let getSelectedTemplateIdFunc = null;
let duplicateSelectedTemplateFunc = null;
let selectionStateRef = null;

// ============= INIT =============

export function initClipboardController(deps) {
    instanceModel = deps.instanceModel;
    definitionModel = deps.definitionModel;
    connectionModel = deps.connectionModel;
    renderDeviceFunc = deps.renderDevice;
    renderAllCablesFunc = deps.renderAllCables;
    updateConnectedPortsFunc = deps.updateConnectedPorts;
    renderLibraryFunc = deps.renderLibrary;
    scheduleAutosaveFunc = deps.scheduleAutosave;
    recordActionFunc = deps.recordAction;
    selectDeviceFunc = deps.selectDevice;
    updateSelectionButtonsFunc = deps.updateSelectionButtons;
    getTemplateForDeviceFunc = deps.getTemplateForDevice;
    getConnectionSourceGroupFunc = deps.getConnectionSourceGroup;
    getConnectionTargetGroupFunc = deps.getConnectionTargetGroup;
    findOpenPositionFunc = deps.findOpenPosition;
    getSelectedTemplateIdFunc = deps.getSelectedTemplateId;
    duplicateSelectedTemplateFunc = deps.duplicateSelectedTemplate;
    selectionStateRef = deps.selectionState;
}

// ============= URL HELPERS =============

/**
 * Wrap a share string as a shareable URL
 * @param {string} shareString - The PW1~ share string
 * @returns {string} URL-wrapped share string
 */
function wrapAsShareableUrl(shareString) {
    return PATCHWERK_URL_PREFIX_PROD + shareString;
}

/**
 * Strip URL prefix from a share string if present
 * @param {string} text - Potentially URL-wrapped share string
 * @returns {string} Raw share string (PW1~...) or original text if not a URL
 */
function stripUrlPrefix(text) {
    if (!text) return text;
    const trimmed = text.trim();
    
    // Check for production URL prefix
    if (trimmed.startsWith(PATCHWERK_URL_PREFIX_PROD)) {
        return trimmed.slice(PATCHWERK_URL_PREFIX_PROD.length);
    }
    
    // Check for dev URL prefix
    if (trimmed.startsWith(PATCHWERK_URL_PREFIX_DEV)) {
        return trimmed.slice(PATCHWERK_URL_PREFIX_DEV.length);
    }
    
    return trimmed;
}

// ============= SHARED HELPERS =============

/**
 * Format message for device/connection count
 * @param {number} devCount - Number of devices
 * @param {number} connCount - Number of connections
 * @param {string} verb - Action verb (Copied/Pasted/Imported/Duplicated/Cut)
 * @returns {string} Formatted message
 */
function formatCountMessage(devCount, connCount, verb) {
    const devPart = `${verb} ${devCount} device${devCount > 1 ? 's' : ''}`;
    return connCount > 0 
        ? `${devPart} + ${connCount} connection${connCount > 1 ? 's' : ''}` 
        : devPart;
}

/**
 * Count internal connections among a set of device IDs
 * @param {Array<string>} deviceIds - Device IDs to check
 * @returns {number} Connection count
 */
function countInternalConnections(deviceIds) {
    const idSet = new Set(deviceIds);
    return connectionModel.filter(c => 
        idSet.has(c.sourceDeviceId) && idSet.has(c.targetDeviceId)
    ).length;
}

/**
 * Import templates with deduplication by content hash
 * @param {Array<Object>} templates - Templates to import
 * @returns {Object} Map of old template ID to new template ID
 */
function importTemplatesWithDedup(templates) {
    const templateIdMap = {};
    templates.forEach(t => {
        const contentHash = getTemplateContentHash(t);
        const existing = findTemplateByContentHash(contentHash);
        
        if (existing) {
            templateIdMap[t.id] = existing.id;
        } else {
            const newTemplate = { ...t, id: generateId() };
            if ('isRouter' in t) {
                newTemplate.isRouter = !!t.isRouter;
            }
            templateIdMap[t.id] = newTemplate.id;
            definitionModel.add(newTemplate);
        }
    });
    return templateIdMap;
}

/**
 * Create devices from patch data with template ID remapping
 * @param {Array<Object>} devices - Device data
 * @param {Object} templateIdMap - Template ID remapping
 * @param {Object} offset - Position offset {x, y}
 * @returns {Object} { newDevices, deviceIdMap }
 */
function createDevicesFromData(devices, templateIdMap, offset = { x: 0, y: 0 }) {
    const deviceIdMap = {};
    const newDevices = [];
    
    devices.forEach(d => {
        const newDevice = {
            id: generateId(),
            templateId: templateIdMap[d.templateId] || d.templateId,
            position: {
                x: d.position.x + offset.x,
                y: d.position.y + offset.y
            }
        };
        if (d.groupLayout) {
            newDevice.groupLayout = { ...d.groupLayout };
        }
        deviceIdMap[d.id] = newDevice.id;
        newDevices.push(newDevice);
        instanceModel.add(newDevice);
        renderDeviceFunc(newDevice);
    });
    
    return { newDevices, deviceIdMap };
}

/**
 * Create connections with device ID remapping
 * @param {Array<Object>} connections - Connection data
 * @param {Object} deviceIdMap - Device ID remapping
 */
function createConnectionsFromData(connections, deviceIdMap) {
    connections.forEach(c => {
        const newConnection = {
            ...c,
            id: generateId(),
            sourceDeviceId: deviceIdMap[c.sourceDeviceId] || c.sourceDeviceId,
            targetDeviceId: deviceIdMap[c.targetDeviceId] || c.targetDeviceId
        };
        connectionModel.add(newConnection);
    });
}

/**
 * Finalize paste/import: render, autosave
 */
function finalizePaste() {
    renderAllCablesFunc?.();
    updateConnectedPortsFunc?.();
    renderLibraryFunc();
    scheduleAutosaveFunc();
}

/**
 * Get viewport center in canvas coordinates
 * @returns {Object} {x, y}
 */
function getViewportCenter() {
    const container = document.querySelector('.canvas-container');
    const rect = container?.getBoundingClientRect();
    return rect 
        ? screenToCanvas(rect.left + rect.width / 2, rect.top + rect.height / 2) 
        : { x: 100, y: 100 };
}

/**
 * Try to read share string from system clipboard
 * Handles both raw PW1~ strings and URL-wrapped strings
 * @returns {Promise<Object|null>} Parsed patch data or null
 */
async function readShareStringFromClipboard() {
    try {
        const text = await navigator.clipboard.readText();
        if (!text) return null;
        
        // Strip URL prefix if present
        const shareString = stripUrlPrefix(text);
        
        if (shareString?.startsWith('PW1~')) {
            return await fromShareString(shareString);
        }
    } catch (err) {
        // Clipboard access denied or parse error
    }
    return null;
}

// ============= STATE ACCESSORS =============

export function hasClipboardContent() {
    return clipboard !== null || hasSelectionClipboard();
}

export function getClipboardState() {
    return {
        hasContent: hasClipboardContent(),
        hasSingleDevice: clipboard !== null,
        hasSelection: hasSelectionClipboard()
    };
}

export function getSelectionClipboard() {
    return selectionClipboard;
}

export function hasSelectionClipboard() {
    return selectionClipboard?.devices?.length > 0;
}

// ============= TEMPLATE DEDUPLICATION =============

export function getTemplateContentHash(template) {
    const portKey = template.portGroups.map(g => 
        `${g.name}|${g.direction}|${g.type}|${g.count}|${g.offset || 0}|${JSON.stringify(g.labels || [])}|${JSON.stringify(g.stereoPairs || [])}`
    ).join('::');
    return `${template.name}|${template.manufacturer || ''}|${template.version || ''}|${portKey}`;
}

export function findTemplateByContentHash(contentHash) {
    return definitionModel.getAll().find(t => getTemplateContentHash(t) === contentHash);
}

// ============= SINGLE DEVICE CLIPBOARD =============

export function copyDevice(deviceId) {
    const device = instanceModel.getById(deviceId);
    if (!device) return false;
    
    clipboard = {
        templateId: device.templateId,
        position: { x: device.position.x, y: device.position.y },
        groupLayout: device.groupLayout ? { ...device.groupLayout } : null
    };
    updateSelectionButtonsFunc();
    return true;
}

export function pasteDevice(offsetX = 25, offsetY = 25) {
    if (!clipboard) {
        showToast('Nothing to paste', true);
        return null;
    }
    
    const template = definitionModel.getById(clipboard.templateId);
    if (!template) {
        showToast('Template no longer exists', true);
        clipboard = null;
        return null;
    }
    
    const newDevice = {
        id: generateId(),
        templateId: clipboard.templateId,
        position: {
            x: clipboard.position.x + offsetX,
            y: clipboard.position.y + offsetY
        }
    };
    
    if (clipboard.groupLayout) {
        newDevice.groupLayout = { ...clipboard.groupLayout };
    }
    
    instanceModel.add(newDevice);
    renderDeviceFunc(newDevice);
    selectDeviceFunc(newDevice.id);
    renderLibraryFunc();
    scheduleAutosaveFunc();
    
    // Record undo action
    const deviceCopy = JSON.parse(JSON.stringify(newDevice));
    const selectedDevices = getSelectedDevices();
    recordActionFunc(
        'paste-device',
        `Paste ${template.name}`,
        () => {
            instanceModel.remove(deviceCopy.id);
            document.getElementById(deviceCopy.id)?.remove();
            selectedDevices.delete(deviceCopy.id);
            updateSelectionVisuals();
            renderLibraryFunc();
            scheduleAutosaveFunc();
        },
        () => {
            instanceModel.add(JSON.parse(JSON.stringify(deviceCopy)));
            renderDeviceFunc(deviceCopy);
            selectDeviceFunc(deviceCopy.id);
            renderLibraryFunc();
            scheduleAutosaveFunc();
        }
    );
    
    clipboard.position.x += offsetX;
    clipboard.position.y += offsetY;
    
    return newDevice;
}

export function duplicateDevice(deviceId, offsetX = 25, offsetY = 25) {
    if (copyDevice(deviceId)) {
        const newDevice = pasteDevice(offsetX, offsetY);
        if (newDevice) {
            showToast('Instance duplicated (linked)');
            return newDevice;
        }
    }
    return null;
}

// ============= SELECTION CLIPBOARD (MULTI-DEVICE) =============

export function copySelection() {
    const deviceIds = Array.from(getSelectedDevices());
    if (deviceIds.length === 0) return false;
    
    const devices = deviceIds.map(id => instanceModel.getById(id)).filter(Boolean);
    const selectedSet = getSelectedDevices();
    const internalConnections = connectionModel.filter(c =>
        selectedSet.has(c.sourceDeviceId) && selectedSet.has(c.targetDeviceId)
    );
    
    const bounds = getSelectionBounds(devices);
    
    selectionClipboard = {
        devices: devices.map(d => ({
            originalId: d.id,
            templateId: d.templateId,
            relativePosition: {
                x: d.position.x - bounds.centerX,
                y: d.position.y - bounds.centerY
            },
            groupLayout: d.groupLayout ? { ...d.groupLayout } : null
        })),
        connections: internalConnections.map(c => ({
            sourceDeviceId: c.sourceDeviceId,
            sourceGroupIndex: getConnectionSourceGroupFunc(c),
            sourcePortType: c.sourcePortType,
            sourcePortIndex: c.sourcePortIndex,
            targetDeviceId: c.targetDeviceId,
            targetGroupIndex: getConnectionTargetGroupFunc(c),
            targetPortType: c.targetPortType,
            targetPortIndex: c.targetPortIndex
        })),
        originalCenter: { x: bounds.centerX, y: bounds.centerY },
        pasteOffset: { x: 25, y: 25 }
    };
    
    updateSelectionButtonsFunc();
    return true;
}

export function pasteSelection() {
    if (!hasSelectionClipboard()) {
        showToast('Nothing to paste', true);
        return null;
    }
    
    // Check templates exist
    const missingTemplates = selectionClipboard.devices.filter(d =>
        !definitionModel.exists(d.templateId)
    );
    if (missingTemplates.length > 0) {
        showToast('Some templates no longer exist', true);
        return null;
    }
    
    const pasteCenter = {
        x: selectionClipboard.originalCenter.x + selectionClipboard.pasteOffset.x,
        y: selectionClipboard.originalCenter.y + selectionClipboard.pasteOffset.y
    };
    
    // Create devices
    const deviceIdMap = {};
    const newDevices = [];
    
    selectionClipboard.devices.forEach(clipDevice => {
        const newDevice = {
            id: generateId(),
            templateId: clipDevice.templateId,
            position: {
                x: pasteCenter.x + clipDevice.relativePosition.x,
                y: pasteCenter.y + clipDevice.relativePosition.y
            }
        };
        if (clipDevice.groupLayout) {
            newDevice.groupLayout = { ...clipDevice.groupLayout };
        }
        deviceIdMap[clipDevice.originalId] = newDevice.id;
        newDevices.push(newDevice);
        instanceModel.add(newDevice);
        renderDeviceFunc(newDevice);
    });
    
    // Recreate connections
    selectionClipboard.connections.forEach(clipConn => {
        const newSourceId = deviceIdMap[clipConn.sourceDeviceId];
        const newTargetId = deviceIdMap[clipConn.targetDeviceId];
        
        if (newSourceId && newTargetId) {
            const sourceGroupIndex = clipConn.sourceGroupIndex ?? clipConn.sourceGroupId ?? 0;
            const targetGroupIndex = clipConn.targetGroupIndex ?? clipConn.targetGroupId ?? 0;
            
            connectionModel.add({
                id: generateId(),
                sourceDeviceId: newSourceId,
                sourceGroupIndex: typeof sourceGroupIndex === 'number' ? sourceGroupIndex : 0,
                sourcePortType: clipConn.sourcePortType,
                sourcePortIndex: clipConn.sourcePortIndex,
                targetDeviceId: newTargetId,
                targetGroupIndex: typeof targetGroupIndex === 'number' ? targetGroupIndex : 0,
                targetPortType: clipConn.targetPortType,
                targetPortIndex: clipConn.targetPortIndex
            });
        }
    });
    
    selectionClipboard.pasteOffset.x += 25;
    selectionClipboard.pasteOffset.y += 25;
    
    setDeviceSelection(newDevices.map(d => d.id));
    finalizePaste();
    
    // Record undo action
    const pastedDevicesCopy = newDevices.map(d => JSON.parse(JSON.stringify(d)));
    const pastedConnections = connectionModel.filter(c => 
        newDevices.some(d => d.id === c.sourceDeviceId) && 
        newDevices.some(d => d.id === c.targetDeviceId)
    ).map(c => JSON.parse(JSON.stringify(c)));
    
    recordActionFunc(
        'paste-selection',
        formatCountMessage(newDevices.length, 0, 'Paste'),
        () => {
            pastedConnections.forEach(conn => connectionModel.remove(conn.id));
            pastedDevicesCopy.forEach(device => {
                connectionModel.removeByDeviceId(device.id);
                instanceModel.remove(device.id);
                document.getElementById(device.id)?.remove();
            });
            clearDeviceSelection();
            finalizePaste();
        },
        () => {
            pastedDevicesCopy.forEach(device => {
                instanceModel.add(JSON.parse(JSON.stringify(device)));
                renderDeviceFunc(device);
            });
            pastedConnections.forEach(conn => {
                connectionModel.add(JSON.parse(JSON.stringify(conn)));
            });
            setDeviceSelection(pastedDevicesCopy.map(d => d.id));
            finalizePaste();
        }
    );
    
    return newDevices;
}

// ============= SYSTEM CLIPBOARD (CROSS-TAB) =============

export async function copyDeviceToSystemClipboard(deviceId, isTemplate = false) {
    let template, position = { x: 100, y: 100 }, groupLayout = null;
    
    if (isTemplate) {
        template = definitionModel.getById(deviceId);
    } else {
        const device = instanceModel.getById(deviceId);
        if (!device) return false;
        template = getTemplateForDeviceFunc(device);
        position = { x: device.position.x, y: device.position.y };
        groupLayout = device.groupLayout ? { ...device.groupLayout } : null;
    }
    
    if (!template) return false;
    
    const deviceInstance = { id: generateId(), templateId: template.id, position };
    if (groupLayout) deviceInstance.groupLayout = groupLayout;
    
    const patchData = createPatchData({
        templates: [template],
        devices: [deviceInstance],
        connections: []
    });
    
    try {
        const shareString = await toShareString(patchData);
        // Wrap as shareable URL so pasting in browser opens Patchwerk
        const shareableUrl = wrapAsShareableUrl(shareString);
        await navigator.clipboard.writeText(shareableUrl);
        return true;
    } catch (err) {
        console.warn('Failed to copy to system clipboard:', err);
        return false;
    }
}

export async function copyLibrarySelectionToClipboard(templateIds) {
    if (!templateIds?.length) return false;
    
    if (templateIds.length === 1) {
        return copyDeviceToSystemClipboard(templateIds[0], true);
    }
    
    const templates = definitionModel.getByIds(templateIds);
    if (templates.length === 0) return false;
    
    const devices = templates.map((template, idx) => ({
        id: generateId(),
        templateId: template.id,
        position: { x: 100 + (idx % 4) * 220, y: 100 + Math.floor(idx / 4) * 180 }
    }));
    
    const patchData = createPatchData({ templates, devices, connections: [] });
    
    try {
        const shareString = await toShareString(patchData);
        // Wrap as shareable URL so pasting in browser opens Patchwerk
        const shareableUrl = wrapAsShareableUrl(shareString);
        await navigator.clipboard.writeText(shareableUrl);
        return true;
    } catch (err) {
        console.warn('Failed to copy library selection to clipboard:', err);
        return false;
    }
}

export async function pasteDeviceFromSystemClipboard() {
    const patchData = await readShareStringFromClipboard();
    return patchData ? importFromPatchData(patchData) : null;
}

function importFromPatchData(patchData) {
    if (!patchData?.templates?.length) return null;
    
    const templateIdMap = importTemplatesWithDedup(patchData.templates);
    const { deviceIdMap } = createDevicesFromData(patchData.devices || [], templateIdMap);
    createConnectionsFromData(patchData.connections || [], deviceIdMap);
    finalizePaste();
    
    return patchData.templates[0];
}

export async function copySelectionToSystemClipboard() {
    const deviceIds = Array.from(getSelectedDevices());
    if (deviceIds.length === 0) return false;
    
    const patchData = extractSubset({
        deviceIds: new Set(deviceIds),
        allDevices: instanceModel.getAll(),
        allTemplates: definitionModel.getAll(),
        allConnections: connectionModel.getAll(),
        internalConnectionsOnly: true
    });
    
    try {
        const shareString = await toShareString(patchData);
        // Wrap as shareable URL so pasting in browser opens Patchwerk
        const shareableUrl = wrapAsShareableUrl(shareString);
        await navigator.clipboard.writeText(shareableUrl);
        return true;
    } catch (err) {
        console.warn('Failed to copy selection to system clipboard:', err);
        return false;
    }
}

export async function pasteSelectionFromSystemClipboard() {
    const patchData = await readShareStringFromClipboard();
    return patchData ? importFromPatchDataAsSelection(patchData) : null;
}

function importFromPatchDataAsSelection(patchData) {
    if (!patchData?.templates?.length || !patchData?.devices?.length) return null;
    
    // Calculate offset to center in viewport
    const viewportCenter = getViewportCenter();
    const minX = Math.min(...patchData.devices.map(d => d.position.x));
    const minY = Math.min(...patchData.devices.map(d => d.position.y));
    const maxX = Math.max(...patchData.devices.map(d => d.position.x));
    const maxY = Math.max(...patchData.devices.map(d => d.position.y));
    const offset = {
        x: viewportCenter.x - (minX + maxX) / 2 + 50,
        y: viewportCenter.y - (minY + maxY) / 2 + 50
    };
    
    const templateIdMap = importTemplatesWithDedup(patchData.templates);
    const { newDevices, deviceIdMap } = createDevicesFromData(patchData.devices, templateIdMap, offset);
    createConnectionsFromData(patchData.connections || [], deviceIdMap);
    
    setDeviceSelection(newDevices.map(d => d.id));
    finalizePaste();
    
    return newDevices;
}

// ============= ORCHESTRATION FUNCTIONS =============

export async function performCopy() {
    if (!hasDeviceSelection()) {
        showToast('Nothing selected to copy', true);
        return false;
    }
    
    copySelection();
    
    try {
        const success = await copySelectionToSystemClipboard();
        const devCount = selectionStateRef?.devices?.size || getSelectedDevices().size;
        const connCount = selectionClipboard?.connections?.length || 0;
        const msg = formatCountMessage(devCount, connCount, 'Copied');
        showToast(success ? msg : msg + ' (local only)');
        return true;
    } catch (err) {
        console.warn('Clipboard write failed:', err);
        showToast('Copied (local only)');
        return true;
    }
}

export async function performCut() {
    if (!hasDeviceSelection()) {
        showToast('Nothing selected to cut', true);
        return false;
    }
    
    copySelection();
    const devCount = selectionStateRef?.devices?.size || getSelectedDevices().size;
    
    try {
        await copySelectionToSystemClipboard();
    } catch (err) {
        console.warn('Clipboard write failed:', err);
    }
    
    deleteSelectedDevices();
    showToast(`Cut ${devCount} device${devCount > 1 ? 's' : ''}`);
    return true;
}

export async function performPaste() {
    // Try system clipboard first
    try {
        const devices = await pasteSelectionFromSystemClipboard();
        if (devices?.length > 0) {
            const connCount = countInternalConnections(devices.map(d => d.id));
            showToast(formatCountMessage(devices.length, connCount, 'Imported'));
            return true;
        }
        
        const imported = await pasteDeviceFromSystemClipboard();
        if (imported) {
            showToast('Device imported!');
            return true;
        }
    } catch (err) {
        console.warn('System clipboard paste failed:', err);
    }
    
    // Fall back to internal clipboards
    if (hasSelectionClipboard()) {
        const pasted = pasteSelection();
        if (pasted?.length > 0) {
            const connCount = selectionClipboard?.connections?.length || 0;
            showToast(formatCountMessage(pasted.length, connCount, 'Pasted'));
            return true;
        }
    }
    
    const pastedDevice = pasteDevice();
    if (pastedDevice) {
        showToast('Device pasted (linked)');
        return true;
    }
    
    showToast('Nothing to paste', true);
    return false;
}

// ============= BUTTON HANDLERS =============

export async function copySelectedToClipboard() {
    const libraryMultiSelect = getLibraryMultiSelect();
    
    // Priority 1: Library multi-select
    if (libraryMultiSelect.selectedItems.size > 0) {
        const templateIds = Array.from(libraryMultiSelect.selectedItems);
        const success = await copyLibrarySelectionToClipboard(templateIds);
        const count = templateIds.length;
        showToast(success 
            ? `Copied ${count} device${count > 1 ? 's' : ''}!` 
            : 'Could not copy to clipboard');
        return;
    }
    
    // Priority 2: Canvas device selection
    if (hasDeviceSelection()) {
        copySelection();
        const success = await copySelectionToSystemClipboard();
        const devCount = selectionStateRef?.devices?.size || getSelectedDevices().size;
        const connCount = selectionClipboard?.connections?.length || 0;
        const msg = formatCountMessage(devCount, connCount, 'Copied');
        showToast(success ? msg : msg + ' (local only)');
        updateSelectionButtonsFunc?.();
        return;
    }
    
    // Priority 3: Single template selected in library
    if (getSelectedTemplateIdFunc?.()) {
        const success = await copyDeviceToSystemClipboard(getSelectedTemplateIdFunc(), true);
        showToast(success ? 'Device copied!' : 'Could not copy to clipboard');
        return;
    }
    
    // Priority 4: Nothing selected - copy entire patch
    const allDevices = instanceModel.getAll();
    if (allDevices.length > 0) {
        const allTemplates = definitionModel.getAll();
        const allConnections = connectionModel.getAll();
        
        // Get only templates used by devices on canvas
        const usedTemplateIds = new Set(allDevices.map(d => d.templateId));
        const usedTemplates = allTemplates.filter(t => usedTemplateIds.has(t.id));
        
        const patchData = createPatchData({
            templates: usedTemplates,
            devices: allDevices,
            connections: allConnections
        });
        
        try {
            const shareString = await toShareString(patchData);
            const shareableUrl = wrapAsShareableUrl(shareString);
            await navigator.clipboard.writeText(shareableUrl);
            const connCount = allConnections.length;
            const msg = formatCountMessage(allDevices.length, connCount, 'Copied entire patch:');
            showToast(msg);
        } catch (err) {
            console.warn('Failed to copy entire patch:', err);
            showToast('Could not copy to clipboard', true);
        }
        return;
    }
    
    // Nothing to copy at all
    showToast('Nothing to copy', true);
}

export function pasteFromClipboard() {
    pasteSelectionFromSystemClipboard().then(devices => {
        if (devices?.length > 0) {
            const connCount = countInternalConnections(devices.map(d => d.id));
            showToast(formatCountMessage(devices.length, connCount, 'Imported'));
        } else {
            return pasteDeviceFromSystemClipboard().then(imported => {
                if (imported) {
                    showToast('Device imported!');
                } else if (hasSelectionClipboard()) {
                    const pasted = pasteSelection();
                    if (pasted?.length > 0) {
                        const connCount = selectionClipboard?.connections?.length || 0;
                        showToast(formatCountMessage(pasted.length, connCount, 'Pasted'));
                    }
                } else {
                    const pasted = pasteDevice();
                    showToast(pasted ? 'Device pasted (linked)' : 'Nothing to paste', !pasted);
                }
                updateSelectionButtonsFunc?.();
            });
        }
        updateSelectionButtonsFunc?.();
    }).catch(() => {
        // Clipboard access denied, try internal clipboards
        if (hasSelectionClipboard()) {
            const pasted = pasteSelection();
            if (pasted?.length > 0) {
                const connCount = selectionClipboard?.connections?.length || 0;
                showToast(formatCountMessage(pasted.length, connCount, 'Pasted'));
            }
        } else {
            const pasted = pasteDevice();
            showToast(pasted ? 'Device pasted (linked)' : 'Nothing to paste', !pasted);
        }
        updateSelectionButtonsFunc?.();
    });
}

export function duplicateSelectedDevice() {
    const singleDevice = getSingleSelectedDevice();
    const libraryMultiSelect = getLibraryMultiSelect();
    
    if (singleDevice) {
        duplicateDevice(singleDevice);
    } else if (hasMultipleDevicesSelected()) {
        if (copySelection()) {
            const pasted = pasteSelection();
            if (pasted?.length > 0) {
                const connCount = selectionClipboard?.connections?.length || 0;
                showToast(formatCountMessage(pasted.length, connCount, 'Duplicated'));
            }
        }
    } else if (libraryMultiSelect.selectedItems.size > 0 || getSelectedTemplateIdFunc?.()) {
        duplicateSelectedTemplateFunc?.();
    }
}

// ============= DEVICE EXPORT/IMPORT =============

export async function exportSelectedDevice() {
    const libraryMultiSelect = getLibraryMultiSelect();
    const templateId = getSelectedTemplateIdFunc?.() || 
        (libraryMultiSelect.selectedItems.size > 0 ? Array.from(libraryMultiSelect.selectedItems)[0] : null);
    
    if (!templateId) {
        showToast('Select a device in the library first', true);
        return;
    }
    
    const template = definitionModel.getById(templateId);
    if (!template) {
        showToast('Device not found', true);
        return;
    }
    
    try {
        const patchData = createPatchData({ templates: [template], devices: [], connections: [] });
        const shareString = await toShareString(patchData);
        const blob = new Blob([shareString], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        const fileName = (template.manufacturer ? template.manufacturer + '-' : '') + template.name;
        a.download = fileName.replace(/[^a-zA-Z0-9-_]/g, '_') + '.pw';
        a.click();
        
        URL.revokeObjectURL(url);
        showToast('Device exported');
    } catch (err) {
        showToast('Export failed: ' + err.message, true);
    }
}

export function importDeviceFromFile() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pw';
    
    input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onload = async (loadEvent) => {
            try {
                const content = loadEvent.target.result.trim();
                
                if (!content.startsWith('PW1~')) {
                    showToast('Invalid file format. Expected .pw share string format.', true);
                    return;
                }
                
                const data = await fromShareString(content);
                
                if (!data.templates?.length) {
                    showToast('No device templates found in file', true);
                    return;
                }
                
                let importedCount = 0;
                data.templates.forEach(t => {
                    const existing = definitionModel.getAll().find(e => e.name === t.name);
                    if (!existing) {
                        const newTemplate = { ...t, id: generateId() };
                        if ('isRouter' in t) newTemplate.isRouter = !!t.isRouter;
                        definitionModel.add(newTemplate);
                        importedCount++;
                    }
                });
                
                renderLibraryFunc?.();
                scheduleAutosaveFunc?.();
                
                showToast(importedCount > 0 
                    ? `Imported ${importedCount} device${importedCount > 1 ? 's' : ''}`
                    : 'Device already exists in library');
            } catch (err) {
                showToast('Failed to import: ' + err.message, true);
            }
        };
        reader.readAsText(file);
    };
    
    input.click();
}
